// Feature → required-permission map.
//
// This is the FE companion to the backend bundle definitions in
// services/shared/shared_layer/schemas/rbac.py. For every gated feature, we
// list the permission codes that *unlock* visibility — having ANY one of them
// is enough.
//
// Persona rules (orthogonal — applied by the menu's role list):
//   STAFF & BV_ASSOCIATE → must have at least one matching permission below
//   DEVELOPER_ADMIN / ZUMALE_USER → bypass (see usePermission.isDeveloperAdmin)
//   CHANNEL_PARTNER / PROPERTY_BUYER / INTERIOR_DESIGNER → not RBAC-scoped;
//     their menu items are persona-only and not gated by these codes.

import { PERMISSIONS } from "./permissions";

export const FEATURE_PERMISSIONS = {
  // ───────────── Sidebar / main features ─────────────

  // Projects (sidebar): visible to anyone in any bundle (all bundles grant some
  // form of project access — Manage Projects gives create/edit, others give read).
  PROJECTS: [
    PERMISSIONS.PROJECT_CREATE,
    PERMISSIONS.PROJECT_EDIT,
    PERMISSIONS.PROJECT_READ,
    PERMISSIONS.PROJECT_PROPERTIES_READ,
    PERMISSIONS.PROJECT_CHANNEL_PARTNERS_READ,
  ],

  // Properties: anyone with property read/edit/delete OR project.properties.read.
  PROPERTIES: [
    PERMISSIONS.PROPERTY_READ,
    PERMISSIONS.PROPERTY_EDIT,
    PERMISSIONS.PROPERTY_DELETE,
    PERMISSIONS.PROJECT_PROPERTIES_READ,
  ],

  // Channel Partners (sidebar): only users with the CP CRUD bundle.
  // (Manage Projects users get the read-only CP tab *inside* a project — not the
  //  standalone sidebar entry. That's why the read-only `project.channel_partners.read`
  //  is intentionally NOT in this list.)
  CHANNEL_PARTNERS_PAGE: [
    PERMISSIONS.CHANNEL_PARTNER_CREATE,
    PERMISSIONS.CHANNEL_PARTNER_EDIT,
    PERMISSIONS.CHANNEL_PARTNER_DELETE,
  ],

  // Virtual Agents (parent + Sales Agent sub-item) → Manage Presales bundle.
  VIRTUAL_AGENTS: [PERMISSIONS.SALES_AGENT_MANAGE],

  // Sales (Leads + Customers + Sales tabs combined). Visible if the user has any
  // lead, sale, or customer-read permission.
  SALES_PAGE: [
    PERMISSIONS.LEAD_CREATE,
    PERMISSIONS.LEAD_EDIT,
    PERMISSIONS.LEAD_DELETE,
    PERMISSIONS.SALE_CREATE,
    PERMISSIONS.SALE_READ,
    PERMISSIONS.SALE_EDIT,
    PERMISSIONS.CUSTOMER_READ,
  ],

  // Reports — surface sales / progress / performance dashboards. Gate on sale.read
  // (Manage Sales) so granular roles don't accidentally see cross-functional metrics.
  REPORTS: [PERMISSIONS.SALE_READ],

  // Approvals (cost-sheet approvals etc.) — a sale-edit-level activity.
  APPROVALS: [PERMISSIONS.SALE_EDIT],

  // Role management lives under Settings → Role Based Access.
  ROLE_MANAGE: [PERMISSIONS.ROLE_MANAGE],

  // ───────────── In-page sub-features (intra-page gates) ─────────────

  // Inside a project detail page: editable Channel Partners tab.
  PROJECT_CP_TAB_EDIT: [
    PERMISSIONS.CHANNEL_PARTNER_CREATE,
    PERMISSIONS.CHANNEL_PARTNER_EDIT,
    PERMISSIONS.CHANNEL_PARTNER_DELETE,
  ],

  // Inside Sales page: tab visibility.
  LEADS_TAB: [PERMISSIONS.LEAD_CREATE, PERMISSIONS.LEAD_EDIT, PERMISSIONS.LEAD_DELETE],
  CUSTOMERS_TAB: [PERMISSIONS.CUSTOMER_READ],
  SALES_TAB: [PERMISSIONS.SALE_CREATE, PERMISSIONS.SALE_READ, PERMISSIONS.SALE_EDIT],
};

export default FEATURE_PERMISSIONS;
